'use client';

import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper/types";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import Subheading from "@/components/ui/Subheading/Subheading";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
};

interface PartnerItem {
	partnerSubheading: string;
	partnerImageSrc: string;
	partnerImageSrcMobile?: string;
	partnerImageAlt: string;
	partnerDescription: string;
}

interface PartnersSectionProps {
	language?: LanguageType;
	sectionSubheading: string;
	sectionHeading: string;
	partners: PartnerItem[];
	id?: string;
}

const PartnersSection: React.FC<PartnersSectionProps> = ({
	language = "english",
	sectionSubheading,
	sectionHeading,
	partners,
	id,
}) => {
	const isLg = useIsLg();
	const isTb = useIsTb();
	const swiperRef = useRef<SwiperType | null>(null);
	const progressBarRef = useRef<HTMLDivElement>(null);
	const sectionRef = useRef<HTMLElement>(null);
	const headingAreaRef = useRef<HTMLDivElement>(null);
	const subheadingRef = useRef<HTMLDivElement>(null);
	const headingRef = useRef<HTMLHeadingElement>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	useGSAP(() => {
		// Heading blur-mask reveal (mirrors Headline.tsx)
		const headingArea = headingAreaRef.current;
		const headingElements = [subheadingRef.current, headingRef.current].filter(Boolean) as HTMLElement[];

		if (headingArea && headingElements.length) {
			gsap.set(headingElements, {
				autoAlpha: 0,
				filter: 'blur(30px)',
				y: +100,
				force3D: true,
			});

			gsap.to(headingElements, {
				autoAlpha: 1,
				filter: 'blur(0px)',
				y: 0,
				duration: 1.0,
				stagger: 0.2,
				ease: "power2.out",
				force3D: true,
				scrollTrigger: {
					trigger: headingArea,
					start: "bottom bottom",
					toggleActions: "play none none none",
				},
			});
		}

		// Cards staggered reveal (mirrors TextBenefitsSection columns)
		const section = sectionRef.current;
		if (section) {
			const cards = section.querySelectorAll('.partner-card');
			if (cards.length) {
				gsap.set(cards, { autoAlpha: 0, force3D: true });
				gsap.to(cards, {
					autoAlpha: 1,
					stagger: { each: 0.35, from: "start" },
					duration: 1,
					ease: "power3.out",
					force3D: true,
					scrollTrigger: {
						trigger: cards[0],
						start: "top 70%",
						toggleActions: "play none none none",
					},
				});
			}
		}
	}, { dependencies: [sectionSubheading, sectionHeading, isTb], revertOnUpdate: true });

	const initSwiperProgress = (swiper: SwiperType) => {
		const updateProgress = () => {
			if (progressBarRef.current) {
				const progress = swiper.progress * 100;
				progressBarRef.current.style.width = `${progress}%`;
			}
		};

		swiper.on("progress", updateProgress);
		swiper.snapGrid = [...swiper.slidesGrid];
		updateProgress();
	};

	const handleNext = () => {
		if (swiperRef.current) {
			swiperRef.current.slideTo(swiperRef.current.activeIndex + 1, 300, true);
		}
	};

	const handlePrev = () => {
		if (swiperRef.current) {
			swiperRef.current.slideTo(swiperRef.current.activeIndex - 1, 300, true);
		}
	};

	const alignment = language === 'arabic' ? 'right' : 'left';

	return (
		<section
			ref={sectionRef}
			id={id}
			// style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
			className="
				w-full
				px-[1.25rem]
				padding-main-x-desktop
				flex items-center justify-center
			"
		>
			<div className="w-full container-large">
				<div
					className="
						w-full flex flex-col
						gap-[2.5rem] lg:gap-[7rem]
					"
				>
					{/* Heading area */}
					<div
						ref={headingAreaRef}
						className={`w-full flex flex-col gap-[1.25rem] lg:gap-[2rem]
						`}
						// style={{ direction: LANGUAGE_TO_DIRECTION[language]}}
						style={{ direction: "ltr" }}
					>
						<div ref={subheadingRef}>
							<Subheading
								subheading={sectionSubheading}
								size={isLg ? 24 : 20}
								alignment={alignment}
								language={language}
							/>
						</div>
						<div className="w-full"
							style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
						>

							<h2
								ref={headingRef}
								// style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
								className="text-text-primary text-44-to-24--neue-june lg:min-w-[37rem] lg:max-w-[68.6875rem] lg:flex-grow"
							>
								{sectionHeading}
							</h2>
						</div>
					</div>

					{/* Partners grid (desktop + tablet) / swiper (mobile) */}
					{isTb ? (
						<div
							style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
							className="w-full grid grid-cols-[repeat(auto-fill,minmax(max(18rem,calc((100%-3rem)/4)),1fr))] gap-x-[1rem] gap-y-[2.5rem]"
						>
							{partners.map((partner, index) => (
								<PartnerCard
									key={index}
									partner={partner}
									language={language}
									isLg={isLg}
								/>
							))}
						</div>
					) : (
						<div className="flex flex-col gap-[2rem]">
							<Swiper
								style={{
									direction: LANGUAGE_TO_DIRECTION[language],
									width: '100%',
								}}
								spaceBetween={12}
								slidesPerView={1}
								onBeforeInit={(swiper) => {
									swiperRef.current = swiper;
								}}
								onAfterInit={(swiper) => {
									setIsBeginning(swiper.isBeginning);
									setIsEnd(swiper.isEnd);
									initSwiperProgress(swiper);
								}}
								onSlideChange={(swiper) => {
									setIsBeginning(swiper.isBeginning);
									setIsEnd(swiper.isEnd);
								}}
								centeredSlidesBounds={false}
								centeredSlides={false}
							>
								{partners.map((partner, index) => (
									<SwiperSlide
										key={index}
										style={{ width: '100%' }}
										className="cursor-grab"
									>
										<PartnerCard
											partner={partner}
											language={language}
											isLg={false}
										/>
									</SwiperSlide>
								))}
							</Swiper>

							{/* Navigation controls */}
							<div
								style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
								className="w-full flex flex-col gap-[1.5rem] px-[0.5rem]"
							>
								<div className="w-full flex justify-end">
									<div style={{ direction: "ltr" }} className="w-fit flex flex-row gap-[0.75rem]">
										<CompactButton
											size={44}
											radius={9999}
											disabled={language === "english" ? isBeginning : isEnd}
											onClick={() => {
												if (language === "english") {
													handlePrev();
												} else {
													handleNext();
												}
											}}
										>
											<CaretLeft />
										</CompactButton>

										<CompactButton
											size={44}
											radius={9999}
											disabled={language === "english" ? isEnd : isBeginning}
											onClick={() => {
												if (language === "english") {
													handleNext();
												} else {
													handlePrev();
												}
											}}
										>
											<CaretRight />
										</CompactButton>
									</div>
								</div>

								<div className="w-full bg-stroke-soft-200 h-[1.5px]">
									<div
										ref={progressBarRef}
										className="bg-theme-yellow-primary-base h-[2px] transition-all duration-300"
									></div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};

interface PartnerCardProps {
	partner: PartnerItem;
	language: LanguageType;
	isLg: boolean;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ partner, language, isLg }) => {
	const alignment = language === 'arabic' ? 'right' : 'left';
	const textAlign = language === 'arabic' ? 'text-right' : 'text-left';

	return (
		<div
			style={{ direction: "ltr" }}
			className={`partner-card flex flex-col gap-[1.25rem] lg:gap-[2.5rem] ${language === 'arabic' ? 'pl-[0.75rem]' : 'pr-[0.75rem]'} ${textAlign}`}
		>
			<div className="flex flex-col gap-[1rem] lg:gap-[2rem]">
				<Subheading
					subheading={partner.partnerSubheading}
					size={isLg ? 20 : 16}
					alignment={alignment}
					language={language}
				/>
				<img
					src={isLg ? partner.partnerImageSrc : (partner.partnerImageSrcMobile || partner.partnerImageSrc)}
					alt={partner.partnerImageAlt}
					className={`h-[2.5rem] lg:h-[3rem] w-auto ${language === 'arabic' ? 'self-end' : 'self-start'}`}
				/>
			</div>
			<p className={`text-18--alexandria text-text-quaternary ${textAlign}`}>
				{partner.partnerDescription}
			</p>
		</div>
	);
};

export default PartnersSection;
